import type { Constructor } from './constructor'
import { LitElement } from 'lit'
import { Subject, Observable } from 'rxjs'
import { classMap } from 'lit/directives/class-map.js'
import { styleMap } from 'lit/directives/style-map.js'
import { discoverComponent } from './discovery.service'
import { suppressDoubleTapZoom } from './double-tap-zoom'
import { ContextEvent as ContextRequestEvent } from '@lit/context'
import { themeContext } from '@schmancy/theme/context'
import type { TSchmancyTheme } from '@schmancy/theme/theme.interface'

export declare class IBaseMixin {
	disconnecting: Subject<boolean>
	classMap: typeof classMap
	styleMap: typeof styleMap
	discover<T extends HTMLElement>(tag: string): Observable<T | null>
	readonly stableId: string
	uid: string
	/**
	 * Current locale from theme context. Use with Intl.NumberFormat/DateTimeFormat.
	 * Defaults to navigator.language if no theme provider is found.
	 * @example new Intl.NumberFormat(this.locale).format(1234.56)
	 */
	readonly locale: string
	dispatchScopedEvent<T>(eventName: string, detail?: T, options?: { bubbles?: boolean; composed?: boolean }): void
}

export const BaseElement = <T extends Constructor<LitElement>>(superClass: T) => {
	class BaseElement extends superClass {
		disconnecting = new Subject<boolean>()
		#stableId?: string
		#uid?: string
		/** True between a disconnect and the next connect, so `connectedCallback`
		 *  knows to re-arm `disconnecting` for the new connection — a completed
		 *  Subject never re-opens. See `disconnectedCallback`/`connectedCallback`. */
		#wasDisconnected = false

		private themeLocale?: string
		private themeUnsubscribe?: () => void
		private readonly receiveTheme = (value: Partial<TSchmancyTheme>, unsubscribe?: () => void): void => {
			if (this.themeUnsubscribe !== unsubscribe) this.themeUnsubscribe?.()
			this.themeUnsubscribe = unsubscribe
			const previous = this.locale
			this.themeLocale = value?.locale
			// Nested providers re-request unchanged subscriptions. Only locale is
			// consumed here, so palette or duplicate delivery cannot earn a render.
			if (this.locale !== previous) this.requestUpdate()
		}

		/** Current locale from theme context. Falls back to navigator.language. */
		get locale(): string {
			return this.themeLocale ?? (typeof navigator !== 'undefined' ? navigator.language : 'de-DE')
		}

		/** Stable ID from DOM path - lazy, only computed on first access */
		get stableId(): string {
			if (this.#stableId) return this.#stableId
			const path: string[] = []
			for (let el: Element | null = this; el?.parentElement && path.length < 5; el = el.parentElement) {
				const tag = el.tagName.toLowerCase()
				const siblings = Array.from(el.parentElement.children).filter(c => c.tagName === el!.tagName)
				path.unshift(siblings.length > 1 ? `${tag}:nth-of-type(${siblings.indexOf(el) + 1})` : tag)
			}
			const hash = Array.from(path.join('>')).reduce((h, c) => (Math.imul(31, h) + c.charCodeAt(0)) | 0, 0)
			return (this.#stableId = `el-${Math.abs(hash).toString(36)}`)
		}

		/**
		 * Unique instance ID - can be overridden via attribute, otherwise auto-generated.
		 * Usage: <my-component uid="custom-id"> or let it auto-generate
		 */
		get uid(): string {
			// Check if uid was set via attribute
			const attrUid = this.getAttribute('uid')
			if (attrUid) return attrUid

			// Auto-generate if not set
			if (!this.#uid) {
				this.#uid = `el-${crypto.randomUUID()}`
			}
			return this.#uid
		}

		set uid(value: string) {
			if (value) {
				this.setAttribute('uid', value)
			} else {
				this.removeAttribute('uid')
			}
		}

		/**
		 * Dispatch an event scoped to this component instance.
		 * Emits BOTH scoped event (eventName::uid) and generic event for backward compatibility.
		 * This prevents event collision between multiple instances of the same component.
		 */
		dispatchScopedEvent<T>(
			eventName: string,
			detail?: T,
			options: { bubbles?: boolean; composed?: boolean } = {},
		): void {
			const { bubbles = false, composed = true } = options

			// Emit scoped event for new code
			this.dispatchEvent(
				new CustomEvent(`${eventName}::${this.uid}`, {
					detail,
					bubbles,
					composed,
				}),
			)

			// Emit generic event for backward compatibility
			this.dispatchEvent(
				new CustomEvent(eventName, {
					detail,
					bubbles,
					composed,
				}),
			)
		}

		/**
		 * Splits every multi-token key into single-token entries before delegating
		 * to Lit's classMap, whose own implementation calls `classList.add()` on
		 * each key verbatim and throws `InvalidCharacterError` on any key holding a
		 * space.
		 *
		 * A token is ON when ANY key that names it is on, which is why this ORs
		 * rather than assigns. Assigning made the LAST key to name a token the only
		 * one that decided it, so a token shared by two mutually-exclusive keys
		 * silently evaporated:
		 *
		 *     'font-mono break-words text-surface-on':      !ambiguous,
		 *     'font-mono break-words text-warning-default':  ambiguous,
		 *
		 * bound `font-mono` to `ambiguous` alone — false for every ordinary string,
		 * so the monospace face the caller asked for in both arms was absent from
		 * one of them. Nothing warned: the class is spelled correctly, tsc sees a
		 * key, and the surface merely renders in the wrong face.
		 */
		classMap(classes: Record<string, boolean>) {
			const newClasses: Record<string, boolean> = {}
			Object.keys(classes).forEach(key => {
				key
					.trim()
					.split(' ')
					.filter(Boolean)
					.forEach(k => {
						newClasses[k] = (newClasses[k] ?? false) || classes[key]
					})
			})
			return classMap(newClasses)
		}

		styleMap(styles: Record<string, string | number>) {
			return styleMap(styles)
		}

		connectedCallback() {
			// Before `super`, which is where Lit builds the render root: the floor
			// belongs to the document the moment a schmancy component is in it, and
			// nothing later in this callback can fail it back open.
			suppressDoubleTapZoom(this.ownerDocument)
			const previousLocale = this.locale
			this.themeLocale = undefined
			super.connectedCallback()
			// A completed Subject never re-opens, so a reconnect (reparent or
			// genuine re-add) gates its subscriptions against a fresh one —
			// otherwise every `takeUntil(this.disconnecting)` wired in this
			// connection's connectedCallback would complete on arrival. Only
			// re-arm after a real disconnect completed the previous one.
			if (this.#wasDisconnected) {
				this.disconnecting = new Subject<boolean>()
				this.#wasDisconnected = false
			}
			this.dispatchEvent(new ContextRequestEvent(themeContext, this, this.receiveTheme, true))
			if (this.locale !== previousLocale) this.requestUpdate()
		}

		/** The nearest ancestor of this element with tag `tag` — a composed-tree climb, not a broadcast; see `discovery.service.ts`. */
		discover<T extends HTMLElement>(tag: string): Observable<T | null> {
			return discoverComponent<T>(tag, this)
		}

		disconnectedCallback() {
			// Synchronous teardown: this connection's subscriptions end the moment
			// the element leaves the DOM. A reparent (schmancy-menu adopting slotted
			// content into the overlay, drag-and-drop, any DOM move) fires
			// disconnectedCallback then connectedCallback and pays this teardown, but
			// connectedCallback re-arms `disconnecting` for the new connection — so a
			// subscription wired in connectedCallback with `takeUntil(this.disconnecting)`
			// (the convention) is torn down here and re-wired against the fresh
			// Subject on reconnect: exactly one live subscription across the move, no
			// duplicates, no dead subs. `disconnecting` is per-connection, not
			// one-shot, so a completed Subject never strands a later reconnect.
			this.disconnecting.next(true)
			this.disconnecting.complete()
			this.#wasDisconnected = true
			this.themeUnsubscribe?.()
			this.themeUnsubscribe = undefined
			super.disconnectedCallback()
		}
	}
	return BaseElement as Constructor<IBaseMixin> & T
}
